// The tickets list filters by "opened between two dates" and its presets ("today / week / month") are Jerusalem calendar days,
// but the backend compares the value with the UTC timestamp of `opened_at` and reads a value WITHOUT an offset as UTC. Sending
// `2026-09-21T00:00:00` therefore meant 03:00 Jerusalem time (02:00 in winter): "today" missed tickets opened 00:00-03:00 local
// time and included 00:00-03:00 of the next day. These helpers give the real instants (ISO with Z).

const ZONE = "Asia/Jerusalem";

const formatter = new Intl.DateTimeFormat("en-US", {
  timeZone: ZONE, hourCycle: "h23", year: "numeric", month: "2-digit", day: "2-digit",
  hour: "2-digit", minute: "2-digit", second: "2-digit",
});

// Offset of the zone from UTC at an instant, in milliseconds (positive east of UTC).
function zoneOffsetMs(instantMs) {
  const parts = {};
  for (const { type, value } of formatter.formatToParts(new Date(instantMs))) parts[type] = Number(value);
  const asUtc = Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second);
  return asUtc - Math.floor(instantMs / 1000) * 1000;
}

/** The instant (ISO, Z) of 00:00:00 or 23:59:59 on the Jerusalem calendar day `YYYY-MM-DD`. */
export function jerusalemDayBound(dateString, bound) {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateString || "");
  if (!match) return dateString;                       // not a plain date: leave whatever it is untouched
  const [, y, m, d] = match.map(Number);
  const calendar = new Date(Date.UTC(y, m - 1, d));
  if (calendar.getUTCFullYear() !== y || calendar.getUTCMonth() !== m - 1 || calendar.getUTCDate() !== d) return dateString;   // 2026-02-31 is not a day
  const end = bound === "end";
  const wall = Date.UTC(y, m - 1, d, end ? 23 : 0, end ? 59 : 0, end ? 59 : 0);
  // two passes: the offset must be the one in force at the RESULT, which differs from the one at `wall` on a DST change day
  let instant = wall - zoneOffsetMs(wall);
  instant = wall - zoneOffsetMs(instant);
  return new Date(instant).toISOString();
}

const dayFormatter = new Intl.DateTimeFormat("sv", { timeZone: ZONE });

/** The Jerusalem calendar day (YYYY-MM-DD) of an instant. */
export function jerusalemToday(now = new Date()) {
  return dayFormatter.format(now);
}

/**
 * First Jerusalem calendar day of "today" / "week" / "month" exactly as the Reports summary counts them: the week starts on SUNDAY and the
 * month on the 1st (the deep link used a rolling 7 / 30 days, so the list never matched the number that was clicked).
 */
export function jerusalemPeriodStart(period, now = new Date()) {
  const today = jerusalemToday(now);
  if (period === "month") return `${today.slice(0, 7)}-01`;
  if (period === "week") {
    const [y, m, d] = today.split("-").map(Number);
    const day = new Date(Date.UTC(y, m - 1, d));
    day.setUTCDate(day.getUTCDate() - day.getUTCDay());        // getUTCDay of the calendar date: Sunday = 0
    return day.toISOString().slice(0, 10);
  }
  return today;
}
